/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import EventList from './EventList.jsx';
import groupBy from 'lodash.groupby';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';

moment.locale('nb');

function prettifyEvent(event) {

	const {
		county,
		id = event.eventId,
		lat,
		lng,
		suffix = '',
		slug : link = event.eventSlug,
		organization : title = event.location,
		type = false,
		eventLocation = event.location,
		price,
		organizationName = null,
		organization = null,
		teamCount,
		startDateTimestamp,
		endDateTimestamp
	} = event;

	if (isNaN(lat) || isNaN(lng)) {
		console.log("Invalid event:", event);
	}

	return {
		id,
		position: {
			lat: parseFloat(lat),
			lng: parseFloat(lng)
		},
		title,
		county,
		type,
		link,
		suffix,
		price,
		eventLocation,
		organizationName,
		organization,
		teamCount,
		startDateTimestamp,
		endDateTimestamp
	};
}

const stylesArray = [{
	featureType: "water",
	stylers: [
		{ color: "#cfe7f3" }
	]
},{
	featureType: "road",
	elementType: "geometry.fill",
	stylers: [
		{ color: "#e9e0dc",
			visibility: "simplified" }

	]
},{
	featureType: "landscape",
	elementType: "geometry",
	stylers: [
		{ color: "#ffffff" }
	]
},{
	featureType : "landscape.natural.terrain"
},{
	featureType : "road",
	elementType : "geometry.stroke",
	stylers: [
		{ color: "#ffffff" }
	]
},{
	featureType: "poi",
	elementType: "labels",
	stylers: [
		{ visibility : "off" }
	]
},{
	featureType: "poi.park",
	elementType: "geometry",
	stylers: [
		{ color: "#e2e6e2" }
	]
},{
	featureType: "road",
	elementType: "labels",
	stylers: [
		{ visibility : "off" }
	]
}];
const defaultCenter = {
	lat: 63.436265,
	lng: 10.371094
};
const defaultZoom = 5;

function forceArray(object) {
	if (!object || (typeof (object) === 'object' && !Object.keys(object).length)) {
		return [];
	} else if (object.constructor !== Array || typeof (object) === 'string') {
		return [object];
	}
	return object;
}

function checkStatus(response) {
	if (response.ok) {
		return Promise.resolve(response);
	} else {
		return Promise.reject(new Error(response.statusText));
	}
}

function processEventResults(results) {
	const { events : markers, icon, header, groupLeftMenuBy = 'county' } = results;
	let groups;
	if(groupLeftMenuBy === 'curcuit') {
		groups = groupBy(markers, 'curcuitName');
	} else {
		groups = groupBy(markers, 'county');
	}
	const sorted = Object.keys(groups).sort((a, b) => a.localeCompare(b, 'nb')).map(group => {
		const orgProp = groups[group][0].organizationName ? 'organizationName' : 'organization';
		const eventsInCounty = groups[group]
			.sort((a, b) => a.startDateTimestamp - b.startDateTimestamp)
			.sort((a, b) => a[orgProp].localeCompare(b[orgProp], 'nb'));

		return {
			county: group,
			events: eventsInCounty.map((currentEvent, i) => {
				const nextEvent = eventsInCounty[i+1];
				if(nextEvent && currentEvent[orgProp] === nextEvent[orgProp]) {
					currentEvent.suffix = ` (${moment(currentEvent.startDateTimestamp).format('DD.MM.YYYY')})`;
					nextEvent.suffix = ` (${moment(nextEvent.startDateTimestamp).format('DD.MM.YYYY')})`;
				}
				return prettifyEvent(currentEvent);
			})
		};
	});
	return {
		header,
		icon,
		sorted
	};
}

function InfoMarker(props) {
	const { markers, eventroot } = props;

	return (
		<div className="eventInfoWindow">
			{markers.map((marker) => {
				const title =
					marker.type === 'event'
						? marker.eventLocation
						: marker.organization || marker.organizationName;

				const eventIdIndex = marker.link.lastIndexOf('/');
				const eventId = marker.link.slice(eventIdIndex + 1);
				const eventLink = `${eventroot}?eventid=${eventId}`;

				const startDate = moment(marker.startDateTimestamp);
				const endDate = moment(marker.endDateTimestamp);
				const duration = moment.duration(endDate.diff(startDate)).days() + 1; //include both start and end
				const showDuration =
					duration > 0
						? startDate.isSame(endDate, 'month')
							? `${startDate.format('Do')} - ${endDate.format('Do MMM')}`
							: `${startDate.format('Do MMM')} - ${endDate.format('Do MMM')}`
						: false;

				return (
					<div key={marker.id}>
						<h2>
							<a href={`${eventLink}`}>{title}</a>
						</h2>
						{marker.teamCount && marker.teamCount > 0 && (
							<p>Påmeldte lag: {marker.teamCount}</p>
						)}
						{marker.type && <p>Arrangør: {marker.organization}</p>}
						{!marker.type && <p>Sted: {marker.eventLocation}</p>}
						{showDuration && (
							<p>
								{showDuration} ({duration} {duration > 1 ? 'dager' : 'dag'})
							</p>
						)}
						{marker.price && <p>{marker.price} kroner</p>}
						<a className="eventLink" href={`${eventLink}`}>
							Se mer
						</a>
					</div>
				);
			})}
		</div>
	);
}

InfoMarker.propTypes = {
	eventroot: PropTypes.string,
	markers: PropTypes.arrayOf(PropTypes.object)
};

function CustomMarker({ events, icon, onMarkerClick }) {
	return (
		<Marker
			key={events[0].id}
			icon={
				window.google && window.google.maps
					? {
						url: icon,
						scaledSize: new window.google.maps.Size(30, 30)
					}
					: undefined
			}
			position={events[0].position}
			onClick={onMarkerClick}
		/>
	);
}

CustomMarker.propTypes = {
	events: PropTypes.arrayOf(PropTypes.object).isRequired,
	icon: PropTypes.string.isRequired,
	onMarkerClick: PropTypes.func.isRequired
};


class EventMap extends React.Component {
	constructor(props) {
		super(props);

		this.getMarkers = this.getMarkers.bind(this);
		this.handleLoadedMarkers = this.handleLoadedMarkers.bind(this);
		this.onMarkerClick = this.onMarkerClick.bind(this);
		this.onGroupClick = this.onGroupClick.bind(this);
		this.onWindowSizeChange = this.onWindowSizeChange.bind(this);
		this.onCloseInfoWindow = this.onCloseInfoWindow.bind(this);
		this.mapRef = React.createRef();
		this.handleZoomChanged = this.handleZoomChanged.bind(this);

		this.state = {
			markers: [],
			activeMarkersGroup: 0,
			activeMarker: {},
			showingInfoWindow: false,
			width: window.innerWidth, // We use width to disable map view on mobile
			currentZoom: defaultZoom,
			icon: null
		};
	}

	componentDidMount() {
		window.addEventListener('resize', this.onWindowSizeChange);
		this.getMarkers();
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onWindowSizeChange);
	}

	onWindowSizeChange() {
		this.setState({ width: window.innerWidth });
	}

	getMarkers() {
		const config = JSON.parse(this.props.config);

		Promise.all(config.events.map(e =>
			
			fetch(e.endpoint)
			
				.then(checkStatus)
				.then(response => response.text()) // Change this line to read the response as text
				.then(text => {
					if (!text) {
						console.error('Empty response from the server:', e.endpoint);
						return;
					}
					return JSON.parse(text); // Parse the JSON here
				})
				.then(json => (json.enrollments || json.events || json)) // The API isn't consistent
				.then(json => ({ header: e.header, icon: e.icon, events: json, groupLeftMenuBy: e.groupLeftMenuBy }))
				.catch(error => console.log('error fetching events', error))
		)).then(data => {
			this.handleLoadedMarkers(data);
			console.log("Response: ", data);
		});


	}

	handleLoadedMarkers(results) {
		const processed = forceArray(results)
			.filter(result => result !== undefined) // Add this line to filter out undefined results
			.map(processEventResults);

		this.setState({
			markers: processed,
			icon: processed[0].icon
		});
	}

	onGroupClick(group) {
		this.setState({
			activeMarkersGroup: group
		});
	}


	onCloseInfoWindow () {
		this.setState({
			activeMarker: null,
			showingInfoWindow: false
		});
	}

	onMarkerClick(clickedEvent) {
		const { markers, activeMarkersGroup } = this.state;
		const correspondingEvents = markers[activeMarkersGroup].sorted
			.flatMap((county) => county.events)
			.filter((event) => event.position.lat === clickedEvent.position.lat && event.position.lng === clickedEvent.position.lng);

		this.setState({
			activeMarker: correspondingEvents,
			showingInfoWindow: true
		});
	}


	

	renderEventList() {
		const { markers, activeMarkersGroup } = this.state;
		const { eventroot } = this.props;
		if(markers.length > 0) {
			return (
			// eslint-disable-next-line max-len
				<EventList eventroot={eventroot} markers={markers} activeMarkersGroup={activeMarkersGroup} onClick={this.onGroupClick}/>
			);
		}
		return null;
	}

	handleZoomChanged () {
		if (this.mapRef.current) {
			const newZoom = this.mapRef.current.getZoom();
			this.setState ({
				currentZoom: newZoom
			});
		}
	}

	render() {
		const { markers, activeMarkersGroup, activeMarker, showingInfoWindow, width } = this.state;
		const { eventroot } = this.props;
		const isMobile = (width <= 640);
		const activeGroupIcon = markers[activeMarkersGroup] ? markers[activeMarkersGroup].icon : undefined;


		const mapContainerStyle = {
			width: '100%',
			height: '100%'
		};
		
		return (
			<React.Fragment>
				{(!isMobile && markers.length > 0) && (
					<LoadScript googleMapsApiKey={"AIzaSyC1ImTZnLBVtqIvytDDd81KToOG4LWBNh4"}>
						<GoogleMap
							onLoad={(map) => {
								this.mapRef.current = map;
							}}
							className="eventMap"
							mapContainerStyle={mapContainerStyle}
							center={defaultCenter}
							zoom={defaultZoom}
							disableDefaultUI={true}
							options={{ styles: stylesArray,
								mapTypeControl: false }}
							onClick={this.handleMapClicked}
							onZoomChanged={this.handleZoomChanged}
						>
							{markers[activeMarkersGroup].sorted.map((county) =>
								county.events.map((event) => (
									<CustomMarker
										key={event.id}
										events={[event]}
										icon={activeGroupIcon}
										onMarkerClick={() => this.onMarkerClick(event)}
									/>
								))
							)}
							{showingInfoWindow && activeMarker && (
								<InfoWindow
									position={activeMarker[0].position}
									visible={showingInfoWindow}
									onCloseClick={this.onCloseInfoWindow}
								>
									<InfoMarker markers={activeMarker} eventroot={eventroot} />
								</InfoWindow>
							)}
						</GoogleMap>
					</LoadScript>
				)}
				{this.renderEventList()}
			</React.Fragment>
		);
	}
}

EventMap.propTypes = {
	initialPosition: PropTypes.object,
	config: PropTypes.string.isRequired,
	eventroot: PropTypes.string
};

export default EventMap;
